import { defineStore } from 'pinia';

import { canView } from '@/helper/content/permissions';
import { getStories } from '@/service';
import type { Stories } from '@/types/stories';

const SEEN_STORIES_STORAGE_KEY = 'stories.seenIds';

const loadSeenStoryIds = (): number[] => {
  try {
    const stored = localStorage.getItem(SEEN_STORIES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const useStoriesStore = defineStore('stories', {
  state: () => ({
    // Unfiltered API response; permission filtering happens reactively in the
    // `stories` getter so internal stories disappear immediately on logout.
    rawStories: [] as Stories,
    loading: false,
    loaded: false,
    error: null as string | null,
    overlayOpen: false,
    seenStoryIds: loadSeenStoryIds(),
  }),
  getters: {
    stories(state): Stories {
      const story = state.rawStories[0];
      if (!story) {
        return [];
      }
      const children = story.children?.filter((child) => canView(child)) || [];
      return children.length ? [{ ...story, children }] : [];
    },
    getStories(): Stories {
      return this.stories;
    },
    hasStories(): boolean {
      return !!this.stories.length;
    },
    hasUnseenStories(): boolean {
      return this.stories.some((story) =>
        story.children.some((child) => !this.seenStoryIds.includes(child.id)),
      );
    },
    isLoading: (state) => state.loading,
    hasLoaded: (state) => state.loaded,
  },
  actions: {
    setStories(stories: Stories) {
      this.rawStories = stories;
    },
    setLoading(loading: boolean) {
      this.loading = loading;
      this.loaded = !loading;
    },
    setError(error: string | null) {
      this.error = error;
    },
    markStorySeen(storyItemId: number) {
      if (this.seenStoryIds.includes(storyItemId)) {
        return;
      }
      this.seenStoryIds.push(storyItemId);
      try {
        localStorage.setItem(
          SEEN_STORIES_STORAGE_KEY,
          JSON.stringify(this.seenStoryIds),
        );
      } catch {
        // Persistence is best-effort; the in-memory state still works.
      }
    },
    async fetchStories() {
      this.setLoading(true);
      this.setError(null);
      try {
        const response = await getStories();
        this.setStories(response.data);
      } catch (error) {
        this.setError('Failed to fetch stories: ' + (error as Error).message);
      } finally {
        this.setLoading(false);
      }
    },
  },
});
